import { Dialog, DialogTitle } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useActiveClassroom } from '../../active-simulation/useActiveSimulation';
import { useSimulators } from '../../api';
import { hasGroupSimulationAccess } from '../../auth';
import CreateExerciseActions from '../../create-exercise/CreateExerciseActions';
import CreateExerciseStepper from '../../create-exercise/CreateExerciseStepper';
import { useCreateExercise } from '../../create-exercise/useCreateExercise';
import CreateExerciseError from '../../create-exercise-error/CreateExerciseError';
import { useSignalREffect } from '../../signalr/SignalRContext';
import {
  ExerciseFile,
  ExerciseMetaData,
  ExerciseType,
  InstructorClassroomSession,
  LoadingSimulation,
  SimulationEventMessage,
  SimulationRole,
} from '../../types';
import { setFormFieldError } from '../../utils/setFormFieldError';
import { useGetErrorMessage } from '../../utils/useGetErrorMessage';
import CautionStep from './CautionStep';
import ExerciseStep from './ExerciseStep';
import StudentsStep from './StudentsStep';
import TypeStep from './TypeStep';
import { useStartSimulation } from './useStartSimulation';

export interface CreateAdHocExerciseDialogProps {
  onClose: (reason?: string) => void;
  onConfirmClassroom: (activeClassroom: InstructorClassroomSession) => void;
  onConfirmSimulation: (activeSimulation: LoadingSimulation) => void;
  open: boolean;
}

export interface AdHocExerciseFormData {
  adHoc: boolean;
  collectionId: string | null;
  description: string;
  endTime: Date | null;
  exercises: ExerciseFile[];
  exerciseId: string | null;
  exercisesIds: string[] | null;
  exercisesMetaData: ExerciseMetaData[] | null;
  layout: string;
  limitNumberOfAttempts: boolean;
  maxNumberOfAttempts: string;
  moduleId: string | null;
  studentEnvironment: { name: string; position?: string } | null;
  simulator: string;
  startTime: Date | null;
  timeRange: string; // Virtual field for setting errors
  timezone: string | null;
  title: string;
  type: ExerciseType;
  users: string[];
  visibility: boolean;
  vessel: {
    vesselCode: string | null;
    vesselName: string | null;
  } | null;
}

const formFields: (keyof AdHocExerciseFormData)[] = [
  'description',
  'exercises',
  'layout',
  'studentEnvironment',
  'simulator',
  'title',
  'type',
  'users',
  'vessel',
  'visibility',
];

const selfStudySteps: { fields: Array<keyof AdHocExerciseFormData>; label: string }[] = [
  { fields: ['type'], label: 'Type' },
  { fields: ['title', 'description', 'simulator', 'exercises', 'collectionId', 'moduleId'], label: 'Exercise' },
];
const classroomSteps: { fields: Array<keyof AdHocExerciseFormData>; label: string }[] = [
  { fields: ['type'], label: 'Type' },
  { fields: [], label: 'Caution' },
  { fields: ['title', 'description', 'simulator', 'exercises', 'collectionId', 'moduleId'], label: 'Exercise' },
  { fields: ['users'], label: 'Students' },
];
// Users without group-simulation access never see the Type step, so Exercise becomes step 0.
const restrictedSteps = selfStudySteps.slice(1);

export const getAdHocSteps = (canViewGroupSimulations: boolean, type: ExerciseType) => {
  if (!canViewGroupSimulations) {
    return restrictedSteps;
  }
  return type === ExerciseType.Classroom ? classroomSteps : selfStudySteps;
};

const classroomStepComponents = [TypeStep, CautionStep, ExerciseStep, StudentsStep];
const selfStudyStepComponents = [TypeStep, ExerciseStep];
const restrictedStepComponents = selfStudyStepComponents.slice(1);

export const getAdHocStepComponents = (canViewGroupSimulations: boolean, type: ExerciseType) => {
  if (!canViewGroupSimulations) {
    return restrictedStepComponents;
  }
  return type === ExerciseType.Classroom ? classroomStepComponents : selfStudyStepComponents;
};

const CreateExerciseDialog = ({
  onClose,
  onConfirmClassroom,
  onConfirmSimulation,
  open,
}: CreateAdHocExerciseDialogProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [creatingClassroom, setCreatingClassroom] = useState(false);

  const form = useForm<AdHocExerciseFormData>({
    defaultValues: {
      description: '',
      endTime: null,
      exerciseId: null,
      layout: '',
      limitNumberOfAttempts: false,
      maxNumberOfAttempts: '',
      studentEnvironment: {},
      simulator: '',
      startTime: null,
      timezone: null,
      title: '',
      type: ExerciseType.SelfStudy,
      users: [],
      vessel: null,
      visibility: false,
    },
  });

  const [exerciseId, exercises, simulatorId, type] = form.watch(['exerciseId', 'exercises', 'simulator', 'type']);
  const canViewGroupSimulations = hasGroupSimulationAccess();
  const { activeClassroom: classroom } = useActiveClassroom();
  const { simulators } = useSimulators(type);
  const selectedExercise = exercises?.find((exercise: ExerciseFile) => exercise.id === exerciseId);
  const simulator = simulators?.find(e => e.id === simulatorId);

  useSignalREffect(
    SimulationEventMessage.ClassroomUpdated,
    (...args) => {
      const activeClassroom = args[0] as InstructorClassroomSession | null;
      // TO avoid double redirection I will check for active classroom in the react state "!classroom"
      const hasOngoingClassroom = activeClassroom && !classroom;
      if (hasOngoingClassroom) {
        setCreatingClassroom(false);
        onConfirmClassroom(activeClassroom);
      }
    },
    [],
  );

  const steps = getAdHocSteps(canViewGroupSimulations, type);
  const stepNames = steps.map(step => step.label);

  const { startSimulation, isStartingSimulation, startSimulationError } = useStartSimulation({
    async onError(error) {
      // Extract error message for inline display
      const body = await error.clone().json();
      if (body.errors) {
        setFormFieldError(body.errors, formFields, form.setError);
      }
    },
    onSuccess(id) {
      onConfirmSimulation({
        id,
        exercise: { name: selectedExercise!.title, type },
        message: 'Starting simulator',
        progress: 0,
        role: SimulationRole.Instructor,
        simulator: simulator!,
      });
    },
  });

  const {
    createExerciseError,
    isCreatingExercise,
    createExercise,
    showCreateExerciseError,
    setShowCreateExerciseError,
  } = useCreateExercise(form.setError);

  const isLoading = isStartingSimulation || isCreatingExercise || creatingClassroom;
  const [startSimulationErrorMessage, showStartSimulationError, setShowStartSimulationError] = useGetErrorMessage(
    startSimulationError ?? undefined,
  );

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleNext: SubmitHandler<AdHocExerciseFormData> = (data, e) => {
    e?.preventDefault();

    if (activeStep < stepNames.length - 1) {
      setActiveStep(prevActiveStep => prevActiveStep + 1);
    } else if (type === ExerciseType.SelfStudy) {
      startSimulation({
        exercise: exerciseId,
        layout: data.layout,
        simulator: data.simulator,
        position: data.studentEnvironment?.name || null,
        vessel: data?.vessel,
      });
    } else if (type === ExerciseType.Classroom) {
      setCreatingClassroom(true);
      createExercise({ ...data, adHoc: true });
    }
  };

  useEffect(() => {
    if (open) {
      form.reset();
      setActiveStep(0);
      setShowCreateExerciseError(false);
      setShowStartSimulationError(false);
    }
  }, [open]);

  const stepComponents = getAdHocStepComponents(canViewGroupSimulations, type);
  const StepComponent = stepComponents[activeStep];

  return (
    <Dialog open={open} fullWidth onClose={(_, reason) => onClose(reason)}>
      <DialogTitle>Simulate now</DialogTitle>
      <StepComponent
        {...form}
        adHoc
        actions={
          <CreateExerciseActions
            activeStep={activeStep}
            finishLabel={type === ExerciseType.SelfStudy ? 'Start simulation' : 'Start session'}
            isLoading={isLoading}
            handleBack={handleBack}
            handleCancel={onClose}
            simulators={simulators ?? []}
            steps={stepNames}
            type={type}
          />
        }
        handleNext={handleNext}
        simulators={simulators}
        stepper={
          <CreateExerciseStepper
            activeStep={activeStep}
            errors={form.formState.errors}
            steps={steps}
            setActiveStep={setActiveStep}
          />
        }
      />
      {showCreateExerciseError && <CreateExerciseError error={createExerciseError} />}
      {showStartSimulationError && !Object.keys(form.formState.errors).length && (
        <CreateExerciseError error={startSimulationErrorMessage} />
      )}
    </Dialog>
  );
};

export default CreateExerciseDialog;
